import { useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import {
  BillingDetailsForm,
  BillingDetailsFormHandle,
} from 'features/subscription/components/billing-details-form';
import { primaryButtonClass, secondaryButtonClass } from 'features/subscription/constants/button-styles';

interface TopUpBillingDialogProps {
  open: boolean;
  /** The formatted top-up amount, shown on the pay button. */
  amount: string;
  onClose: () => void;
  /** Called once the details are saved, so the caller can go on with the payment. */
  onSaved: () => void;
}

/** Asks for the billing details a top-up needs, the same way the plan checkout dialog does. */
export const TopUpBillingDialog = ({ open, amount, onClose, onSaved }: TopUpBillingDialogProps) => {
  const { t } = useTranslation();
  const isDark = useTheme().resolvedTheme === 'dark';
  const billingRef = useRef<BillingDetailsFormHandle>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleContinue = async () => {
    setIsSaving(true);
    const isSaved = await billingRef.current?.save();
    setIsSaving(false);
    if (isSaved) onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSaving && onClose()}>
      {/* Header and footer stay put; only the form scrolls on short screens. */}
      <DialogContent className="!flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[600px]">
        <DialogHeader className="px-6 pb-4 pt-6">
          <DialogTitle>{t('BILLING_DETAILS_NEEDED', 'Billing details are needed to pay')}</DialogTitle>
          <DialogDescription>
            {t('BILLING_SAVED_FOR_NEXT_TIME', 'They go on the invoice and are saved for next time.')}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
          <BillingDetailsForm ref={billingRef} hideActions />
        </div>

        <DialogFooter className="gap-2 border-t px-6 py-4">
          <Button
            variant="outline"
            className={cn('rounded-full px-6', secondaryButtonClass(isDark))}
            onClick={onClose}
            disabled={isSaving}
          >
            {t('CANCEL', 'Cancel')}
          </Button>
          <Button
            className={cn('rounded-full px-6', primaryButtonClass(isDark))}
            onClick={handleContinue}
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('PAY_AND_CONTINUE', { amount, defaultValue: 'Pay {{amount}} and continue' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
